import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, Checkbox, IconButton} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import UndoIcon from "@material-ui/icons/Undo";

import AmendmentsHeader from "./amendments_header";
import buildAmendmentErrorEntry from "./build_amendment_error_entry";
import ClauseChangeItem from "./clause_change";
import StatusBadge from "./status_badge";
import SummaryGroup from "./summary_group";
import groupClauseAmendments from "../../utils/group_clause_amendments";
import {amendmentUserErrorMessage} from "../../utils/user_error_message";
import {applyDocumentAmendments as applyDocumentAmendmentsDirect} from "word/taskpane/routes/checklist/utils/apply_document_amendments";
import fixBadLlmAmendments from "word/taskpane/routes/checklist/utils/amendments/fix_bad_llm_amendments";
import {useTrackChangesCheck} from "./use_track_changes_check";
import usePrevious from "utils/hooks/use_previous";
import {AmendmentClauseTarget} from "utils/amendments/get_amendment_anchor_text";
import type {ApplyDocumentAmendmentsFn} from "./amendments_panel";

import {Clausepart} from "common/types/document";
import {
  AppliedAmendments,
  ClauseChangeError,
  DocumentAmendment,
  PreviewHeading,
  ProcessedAmendment,
  ProcessedClauseChange,
} from "common/flowmaster/types/amendment";
import {Uuid} from "common/types";

const styles = {
  buttonContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: "8px",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#fafafa",
    paddingTop: "8px",
    paddingBottom: "4px",
    zIndex: 1,
  },
  button: {
    borderRadius: "4px",
    fontSize: "0.75rem",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
  buttonExpanded: {
    flex: "1 1 140px",
  },
  summaryList: {
    margin: "0",
    padding: "0",
    listStyleType: "none",
  },
  errorMessage: {
    color: "#d32f2f",
    fontSize: "0.85rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    marginTop: "4px",
  },
  errorIcon: {
    fontSize: "14px",
    color: "#d32f2f",
  },
  summaryContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: "8px 12px",
    backgroundColor: "#fafafa",
  },
} as const;

export interface AmendmentErrorEntry {
  error: string;
  issue_id?: number;
  clause_reference?: string;
  action_type?: string;
  deleted_text?: string;
  added_text?: string;
  before_text?: string;
  after_text?: string;
  conversationId?: number;
  userPrompt?: string;
}

export type OnLogAmendmentErrors = (errors: AmendmentErrorEntry[]) => void;
export type ScrollToAmendmentClause = (target: AmendmentClauseTarget) => void;

interface AmendmentGroupProps {
  amendment: ProcessedAmendment;
  applyClauseChanges: (changes: ProcessedClauseChange[]) => void;
  appliedAmendments: AppliedAmendments;
  clauseChangeErrors: ClauseChangeError[];
  setClauseChangeError?: (clauseChangeId: string, message: string) => void;
  documentClauseparts: Clausepart[];
  documentHeadings?: PreviewHeading[];
  currentIndex?: number;
  totalAmendments?: number;
  onNavigate?: (direction: number) => void;
  defaultExpanded?: boolean;
  defaultShowChanges?: boolean;
  onOpenConversation?: (conversationId: number) => void;
  activeConversationId?: number | null;
  applyDocumentAmendments?: ApplyDocumentAmendmentsFn;
  getIsTrackAllChangesOn?: () => Promise<boolean>;
  enableTrackAllChanges?: () => Promise<void>;
  onLogAmendmentErrors?: OnLogAmendmentErrors;
  bulkApplyingIds?: string[];
  removeAppliedAmendments?: (amendmentIds: string[]) => void;
  scrollToClause?: ScrollToAmendmentClause;
}

const AmendmentGroup: React.FC<AmendmentGroupProps> = ({
  amendment,
  applyClauseChanges,
  appliedAmendments,
  clauseChangeErrors,
  setClauseChangeError,
  documentClauseparts,
  documentHeadings,
  currentIndex,
  totalAmendments,
  onNavigate,
  defaultExpanded = false,
  defaultShowChanges = false,
  onOpenConversation,
  activeConversationId,
  applyDocumentAmendments,
  getIsTrackAllChangesOn,
  enableTrackAllChanges,
  onLogAmendmentErrors,
  bulkApplyingIds,
  removeAppliedAmendments,
  scrollToClause,
}) => {
  const applyAmendmentsFn =
    applyDocumentAmendments ?? applyDocumentAmendmentsDirect;
  const {
    checkTrackChangesBeforeApply,
    trackChangesDialogs,
    isPending,
    resetPending,
  } = useTrackChangesCheck(getIsTrackAllChangesOn, enableTrackAllChanges);
  const [selectedClauseChanges, setSelectedClauseChanges] = useState<Uuid[]>(
    [],
  );
  const [selectedComments, setSelectedComments] = useState<Uuid[]>([]);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showPreview, setShowPreview] = useState(defaultShowChanges);

  const clauseChanges = amendment.clause_changes;

  const processedChanges = useMemo(
    () =>
      groupClauseAmendments(
        clauseChanges,
        documentClauseparts,
        documentHeadings ?? [],
      ),
    [clauseChanges, documentClauseparts, documentHeadings],
  );

  // Text inserted by sibling amendments so previews can avoid showing a chained
  // anchor (which references not-yet-applied text) as if it were document context.
  const siblingAddedTexts = useMemo(
    () => clauseChanges.map(change => change.added_text).filter(Boolean),
    [clauseChanges],
  );

  const expandGroup = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const isAnyBulkApplyActive = Boolean(
    bulkApplyingIds && bulkApplyingIds.length > 0,
  );

  const changeIdSet = useMemo(
    () =>
      new Set(
        processedChanges.flatMap(c =>
          c.grouped && c.related_changes
            ? c.related_changes.map(r => r.id)
            : [c.id],
        ),
      ),
    [processedChanges],
  );

  const isBulkApplying = useMemo(() => {
    if (!bulkApplyingIds || bulkApplyingIds.length === 0) {
      return false;
    }
    return bulkApplyingIds.some(id => changeIdSet.has(id));
  }, [bulkApplyingIds, changeIdSet]);

  const isChangeApplied = useCallback(
    (changeId: string) => Boolean(appliedAmendments[changeId]),
    [appliedAmendments],
  );

  const isChangeFullyApplied = useCallback(
    (change: ProcessedClauseChange) => {
      if (change.grouped && change.related_changes) {
        return change.related_changes.every(rel => isChangeApplied(rel.id));
      }
      return isChangeApplied(change.id);
    },
    [isChangeApplied],
  );

  const isChangeUnapplied = useCallback(
    (change: ProcessedClauseChange) => {
      if (change.grouped && change.related_changes) {
        return change.related_changes.every(rel => !isChangeApplied(rel.id));
      }
      return !isChangeApplied(change.id);
    },
    [isChangeApplied],
  );

  useEffect(() => {
    if (isPending) {
      resetPending();
    }
  }, [appliedAmendments]);

  const isChangeSelected = useCallback(
    (change: ProcessedClauseChange) => {
      if (change.grouped && change.related_changes) {
        return change.related_changes.every(rel =>
          selectedClauseChanges.includes(rel.id),
        );
      }
      return selectedClauseChanges.includes(change.id);
    },
    [selectedClauseChanges],
  );

  useEffect(() => {
    const allChangeIds = clauseChanges.map(change => change.id);
    const allCommentIds = clauseChanges
      .filter(change => change.comment_text)
      .map(change => change.id);
    setSelectedClauseChanges(allChangeIds);
    setSelectedComments(allCommentIds);
  }, [clauseChanges]);

  const handleClauseSelect = useCallback((id: Uuid, checked: boolean) => {
    if (checked) {
      setSelectedClauseChanges(prev => [...prev, id]);
      setSelectedComments(prev => [...prev, id]);
    } else {
      setSelectedClauseChanges(prev => prev.filter(cid => cid !== id));
      setSelectedComments(prev => prev.filter(cid => cid !== id));
    }
  }, []);

  const toggleGroupedChanges = useCallback(
    (relatedIds: string[], checked: boolean) => {
      setSelectedClauseChanges(prev => {
        if (checked) {
          return Array.from(new Set([...prev, ...relatedIds]));
        } else {
          return prev.filter(id => !relatedIds.includes(id));
        }
      });
    },
    [],
  );

  const applyChangesHandler = useCallback(async () => {
    const amendmentToChangeMap = new Map<
      DocumentAmendment,
      ProcessedClauseChange
    >();
    const documentAmendmentsToApply: DocumentAmendment[] = [];

    processedChanges.forEach(change => {
      const changesToProcess =
        change.grouped && change.related_changes
          ? change.related_changes
          : [change];

      changesToProcess.forEach(individualChange => {
        if (
          !selectedClauseChanges.includes(individualChange.id) ||
          isChangeApplied(individualChange.id) ||
          !individualChange.rawAmendment
        ) {
          return;
        }

        const commentApplied = selectedComments.includes(individualChange.id);
        const rawAmendment = individualChange.rawAmendment;

        if (commentApplied && individualChange.comment_text) {
          const amendmentWithComment = {
            ...rawAmendment,
            comment: individualChange.comment_text,
          };
          documentAmendmentsToApply.push(amendmentWithComment);
          amendmentToChangeMap.set(amendmentWithComment, {
            ...individualChange,
            comment_applied: true,
          });
        } else {
          documentAmendmentsToApply.push(rawAmendment);
          amendmentToChangeMap.set(rawAmendment, individualChange);
        }
      });
    });

    if (documentAmendmentsToApply.length === 0) {
      resetPending();
      return;
    }

    const fixedAmendmentsToApply = fixBadLlmAmendments(
      documentAmendmentsToApply,
    );
    const fixedToChangeMap = new Map<
      DocumentAmendment,
      ProcessedClauseChange
    >();
    fixedAmendmentsToApply.forEach((fixed, i) => {
      const original = documentAmendmentsToApply[i];
      const change = amendmentToChangeMap.get(original);
      if (change) {
        fixedToChangeMap.set(fixed, change);
      }
    });

    const successfulChanges: ProcessedClauseChange[] = [];
    const failedAmendmentErrors: AmendmentErrorEntry[] = [];
    const groupConversationId = amendment.conversationId;
    const groupUserPrompt = amendment.userPrompt;

    await applyAmendmentsFn(
      fixedAmendmentsToApply,
      (amendment, errorMessage) => {
        const change = fixedToChangeMap.get(amendment);
        if (change && setClauseChangeError) {
          setClauseChangeError(
            change.id,
            amendmentUserErrorMessage(errorMessage),
          );
        }
        failedAmendmentErrors.push(
          buildAmendmentErrorEntry(
            amendment,
            errorMessage || "Amendment failed to apply",
            groupConversationId,
            groupUserPrompt,
          ),
        );
      },
      (amendment, failures) => {
        const change = fixedToChangeMap.get(amendment);
        const errorMessage = `Amendment partially failed: ${failures
          .map(f => f.reason)
          .join(", ")}`;
        if (change && setClauseChangeError) {
          setClauseChangeError(
            change.id,
            `Amendment partially failed: ${failures
              .map(f => amendmentUserErrorMessage(f.reason))
              .join("; ")}`,
          );
        }
        failedAmendmentErrors.push(
          buildAmendmentErrorEntry(
            amendment,
            errorMessage,
            groupConversationId,
            groupUserPrompt,
          ),
        );
      },
      amendment => {
        const change = fixedToChangeMap.get(amendment);
        if (change) {
          successfulChanges.push(change);
        }
      },
    );

    if (failedAmendmentErrors.length > 0 && onLogAmendmentErrors) {
      onLogAmendmentErrors(failedAmendmentErrors);
    }

    if (successfulChanges.length > 0) {
      applyClauseChanges(successfulChanges);
    } else {
      resetPending();
    }
  }, [
    processedChanges,
    selectedClauseChanges,
    selectedComments,
    isChangeApplied,
    applyClauseChanges,
    setClauseChangeError,
    applyAmendmentsFn,
    resetPending,
    onLogAmendmentErrors,
  ]);

  const availableChanges = processedChanges.filter(
    change => !isChangeFullyApplied(change),
  );
  const availableChangeCount = availableChanges.length;

  const areAllChangesCompleted = availableChangeCount === 0;

  const appliedIdsInGroup = useMemo(
    () => Array.from(changeIdSet).filter(isChangeApplied),
    [changeIdSet, isChangeApplied],
  );
  const hasAppliedChanges = appliedIdsInGroup.length > 0;
  const isPartiallyApplied = hasAppliedChanges && !areAllChangesCompleted;

  const handleReset = useCallback(() => {
    if (removeAppliedAmendments && hasAppliedChanges) {
      removeAppliedAmendments(appliedIdsInGroup);
    }
  }, [removeAppliedAmendments, appliedIdsInGroup]);

  const getErrorForChange = useCallback(
    (change: ProcessedClauseChange): string | undefined => {
      const changeIds =
        change.grouped && change.related_changes
          ? change.related_changes.map(rel => rel.id)
          : [change.id];
      return clauseChangeErrors.find(err =>
        changeIds.includes(err.clauseChangeId),
      )?.message;
    },
    [clauseChangeErrors],
  );

  const allErrors = useMemo(
    () =>
      processedChanges
        .map(c => getErrorForChange(c))
        .filter((msg): msg is string => Boolean(msg)),
    [processedChanges, getErrorForChange],
  );

  const orderedChanges = [...processedChanges].sort(
    (a, b) => Number(isChangeFullyApplied(b)) - Number(isChangeFullyApplied(a)),
  );

  const prevErrorCount = usePrevious(allErrors.length);

  useEffect(() => {
    if (allErrors.length > 0 && !prevErrorCount) {
      setIsExpanded(true);
    }
  }, [allErrors, prevErrorCount]);

  const selectedAvailableCount = processedChanges.filter(
    change => isChangeSelected(change) && !isChangeFullyApplied(change),
  ).length;

  const handleSelectAllToggle = (checked: boolean) => {
    const ids = availableChanges.flatMap(c =>
      c.grouped && c.related_changes
        ? c.related_changes.map(r => r.id)
        : [c.id],
    );
    if (checked) {
      setSelectedClauseChanges(prev => Array.from(new Set([...prev, ...ids])));
      setSelectedComments(prev => Array.from(new Set([...prev, ...ids])));
    } else {
      const removeSet = new Set(ids);
      setSelectedClauseChanges(prev => prev.filter(id => !removeSet.has(id)));
      setSelectedComments(prev => prev.filter(id => !removeSet.has(id)));
    }
  };

  const handleClauseItemSelectChange = useCallback(
    (change: ProcessedClauseChange, checked: boolean) => {
      if (change.grouped && change.related_changes) {
        const relatedIds = change.related_changes.map(rel => rel.id);
        toggleGroupedChanges(relatedIds, checked);
      } else {
        handleClauseSelect(change.id, checked);
      }
    },
    [toggleGroupedChanges, handleClauseSelect],
  );

  const handleClauseItemCommentSelectChange = useCallback(
    (change: ProcessedClauseChange, checked: boolean) => {
      if (checked) {
        setSelectedComments(prev => [...prev, change.id]);
      } else {
        setSelectedComments(prev => prev.filter(id => id !== change.id));
      }
    },
    [],
  );

  const handleIndividualCommentSelectChange = useCallback(
    (commentId: Uuid, checked: boolean) => {
      setSelectedComments(prev => {
        if (checked) {
          return [...prev, commentId];
        } else {
          return prev.filter(id => id !== commentId);
        }
      });
    },
    [],
  );

  const conversationId = amendment.conversationId;

  const handleOpenConversation =
    onOpenConversation && conversationId
      ? () => onOpenConversation(conversationId)
      : undefined;

  return (
    <div style={styles.summaryContainer}>
      {trackChangesDialogs}
      <AmendmentsHeader
        explanation={amendment.explanation}
        explanationMeta={amendment.explanationMeta}
        currentIndex={currentIndex}
        totalAmendments={totalAmendments}
        onNavigate={onNavigate}
        onOpenConversation={handleOpenConversation}
        isConversationActive={
          Boolean(conversationId) && conversationId === activeConversationId
        }
        titlePrefix={
          isExpanded &&
          !areAllChangesCompleted && (
            <Checkbox
              checked={selectedAvailableCount === availableChangeCount}
              indeterminate={
                selectedAvailableCount > 0 &&
                selectedAvailableCount < availableChangeCount
              }
              onChange={e => handleSelectAllToggle(e.target.checked)}
              disabled={isAnyBulkApplyActive}
              style={{padding: "0px", marginLeft: "16px", marginRight: "8px"}}
              inputProps={{
                "aria-label": "Select all amendments in group",
              }}
            />
          )
        }
        titleSuffix={
          <IconButton size="small" onClick={toggleExpanded}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
      />
      {areAllChangesCompleted && !isExpanded && (
        <div style={{marginTop: "4px"}}>
          <StatusBadge applied label="All changes applied" />
        </div>
      )}
      {!isExpanded && allErrors.length > 0 && (
        <div
          style={{...styles.errorMessage, cursor: "pointer"}}
          onClick={expandGroup}
        >
          <ErrorIcon style={styles.errorIcon} />
          Failed to apply all changes — view reasons
          <ExpandMoreIcon style={styles.errorIcon} />
        </div>
      )}
      {isExpanded && (
        <>
          {showPreview ? (
            orderedChanges.map(change => (
              <ClauseChangeItem
                key={change.id}
                clauseChange={change}
                isSelected={isChangeSelected(change)}
                isCommentSelected={selectedComments.includes(change.id)}
                isCompleted={isChangeFullyApplied(change)}
                showUnappliedBadge={
                  isPartiallyApplied && isChangeUnapplied(change)
                }
                errorMessage={getErrorForChange(change)}
                onSelectChange={checked =>
                  handleClauseItemSelectChange(change, checked)
                }
                onCommentSelectChange={checked =>
                  handleClauseItemCommentSelectChange(change, checked)
                }
                onIndividualCommentSelectChange={
                  handleIndividualCommentSelectChange
                }
                selectedRelatedComments={selectedComments}
                scrollToClause={scrollToClause}
                siblingAddedTexts={siblingAddedTexts}
              />
            ))
          ) : (
            <ul style={styles.summaryList}>
              {orderedChanges.map(change => (
                <SummaryGroup
                  key={change.id}
                  change={change}
                  fullyApplied={isChangeFullyApplied(change)}
                  noneApplied={isChangeUnapplied(change)}
                  isPartiallyApplied={isPartiallyApplied}
                  isSelected={isChangeSelected(change)}
                  isAnyBulkApplyActive={isAnyBulkApplyActive}
                  isChangeApplied={isChangeApplied}
                  onSelectChange={checked =>
                    handleClauseItemSelectChange(change, checked)
                  }
                  errorMessage={getErrorForChange(change)}
                  scrollToClause={scrollToClause}
                />
              ))}
            </ul>
          )}
        </>
      )}
      {(!areAllChangesCompleted || isExpanded || hasAppliedChanges) && (
        <div style={styles.buttonContainer}>
          {(isExpanded || (areAllChangesCompleted && hasAppliedChanges)) && (
            <Button
              variant="outlined"
              style={{...styles.button, ...styles.buttonExpanded}}
              onClick={() => {
                if (!isExpanded) {
                  expandGroup();
                }
                setShowPreview(prev => !prev);
              }}
            >
              {showPreview ? "Show Summaries" : "Show Changes"}
            </Button>
          )}
          {!areAllChangesCompleted && (
            <Button
              variant="contained"
              color="primary"
              style={
                isExpanded
                  ? {...styles.button, ...styles.buttonExpanded}
                  : styles.button
              }
              onClick={() => checkTrackChangesBeforeApply(applyChangesHandler)}
              disabled={
                selectedAvailableCount === 0 ||
                isPending ||
                isAnyBulkApplyActive
              }
            >
              {isPending || isBulkApplying
                ? "Applying Changes..."
                : `Apply Changes (${selectedAvailableCount}/${availableChangeCount})`}
            </Button>
          )}
          {removeAppliedAmendments && hasAppliedChanges && (
            <Button
              variant="outlined"
              size="small"
              style={styles.button}
              onClick={handleReset}
              startIcon={<UndoIcon style={{fontSize: "0.875rem"}} />}
            >
              Reset
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AmendmentGroup;
